// Ollama models page (roadmap item 38).
//
// Mounted at `/repos/:id/ollama` for any repository whose `format` is
// `ollama`. Shows the catalog of (model, tag) pairs the repo holds and,
// for the model the operator clicks, the per-layer breakdown — License,
// Modelfile, Template, System prompt, model weights — that's the part of
// an Ollama bundle that matters for compliance review.
//
// The page derives the (project_slug, repo_slug) pair from the loaded
// `Repo` (`useRepoDetail`) plus the cached `Project` list (`useProjects`)
// rather than threading slugs through the URL — the existing `/repos/:id`
// pattern is UUID-keyed, and this keeps that surface stable.

import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Box, Copy, FileText, Layers, ScrollText, Wand2 } from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useProjects, useRepoDetail } from '../../api/client';
import {
  type OllamaLayer,
  type OllamaModelEntry,
  useOllamaModelDetail,
  useOllamaModels,
} from '../../api/ollama';

function bytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// shortHex trims a long sha256 down to the leading characters that fit
// in a table cell while still being recognisably one digest. We never
// strip the `sha256:` prefix here so a copy-paste lands in code that
// expects the canonical OCI digest shape.
function shortHex(d?: string): string {
  if (!d) return '';
  if (d.length <= 19) return d;
  return d.slice(0, 12) + '…' + d.slice(-4);
}

// Ollama-specific layer media types, mapped to the translation key for the
// human label and the icon the catalog UI renders. Falls back to "Layer"
// for anything we don't recognise — there's no harm in surfacing arbitrary
// media types, and forward-compatibility for new Ollama bundle parts comes
// for free.
const LAYER_KIND_BY_MEDIA: Record<string, { labelKey: string; icon: typeof Box }> = {
  'application/vnd.ollama.image.model': {
    labelKey: 'ollamaModels.layerKinds.modelWeights',
    icon: Box,
  },
  'application/vnd.ollama.image.modelfile': {
    labelKey: 'ollamaModels.layerKinds.modelfile',
    icon: FileText,
  },
  'application/vnd.ollama.image.template': {
    labelKey: 'ollamaModels.layerKinds.promptTemplate',
    icon: Wand2,
  },
  'application/vnd.ollama.image.system': {
    labelKey: 'ollamaModels.layerKinds.systemPrompt',
    icon: ScrollText,
  },
  'application/vnd.ollama.image.license': {
    labelKey: 'ollamaModels.layerKinds.license',
    icon: ScrollText,
  },
  'application/vnd.ollama.image.params': {
    labelKey: 'ollamaModels.layerKinds.params',
    icon: Layers,
  },
};

function modelsCountLabel(
  models: { models: unknown[] } | undefined,
  t: (key: string, opts: { count: number }) => string,
): string | undefined {
  if (!models) return undefined;
  return t('ollamaModels.catalog.count', { count: models.models.length });
}

function layerKindLabel(
  mediaType: string,
  t: (key: string) => string,
): { label: string; icon: typeof Box } {
  const entry = LAYER_KIND_BY_MEDIA[mediaType] ?? {
    labelKey: 'ollamaModels.layerKinds.layer',
    icon: Layers,
  };
  return { label: t(entry.labelKey), icon: entry.icon };
}

export function OllamaModels() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: repo, isLoading: repoLoading, error: repoError } = useRepoDetail(id);
  const { data: projects } = useProjects();
  const projectSlug = useMemo(
    () => projects?.find((p) => p.id === repo?.projectId)?.slug,
    [projects, repo?.projectId],
  );
  const { data: models, error: modelsError, isLoading: modelsLoading } = useOllamaModels(
    projectSlug,
    repo?.slug,
  );
  const [selected, setSelected] = useState<OllamaModelEntry | null>(null);
  const detail = useOllamaModelDetail(
    projectSlug,
    repo?.slug,
    selected?.model,
    selected?.tag,
  );

  if (repoLoading) {
    return <div className="p-6 text-fg-muted">{t('ollamaModels.loadingRepo')}</div>;
  }
  if (repoError || !repo) {
    return (
      <div className="p-6 text-danger">
        {t('ollamaModels.loadRepoFailed', { error: String(repoError ?? 'unknown') })}
      </div>
    );
  }
  if (repo.format !== 'ollama') {
    return (
      <div className="p-6">
        <Link
          to={`/repos/${repo.id}`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('ollamaModels.backToRepo')}
        </Link>
        <p className="text-fg-muted">
          <Trans i18nKey="ollamaModels.wrongFormat" components={[<code key="0" className="orb-code" />]} />
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/repos/${repo.id}`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('ollamaModels.backTo', { name: repo.name })}
        </Link>
        <p className="orb-eyebrow text-accent/80 mb-1">{t('ollamaModels.eyebrow')}</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">
          {repo.name}
        </h1>
        <p className="text-fg-muted text-sm mt-2 max-w-3xl">
          {t('ollamaModels.intro')}
        </p>
      </div>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Box className="h-4 w-4 text-accent" />
              {t('ollamaModels.catalog.title')}
            </span>
          }
          description={modelsCountLabel(models, t)}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-fg-muted border-b border-border">
                <th className="px-4 py-2 font-medium">{t('ollamaModels.catalog.table.model')}</th>
                <th className="px-4 py-2 font-medium">{t('ollamaModels.catalog.table.tag')}</th>
                <th className="px-4 py-2 font-medium">{t('ollamaModels.catalog.table.layers')}</th>
                <th className="px-4 py-2 font-medium">
                  {t('ollamaModels.catalog.table.totalSize')}
                </th>
                <th className="px-4 py-2 font-medium">
                  {t('ollamaModels.catalog.table.manifestDigest')}
                </th>
                <th className="px-4 py-2 font-medium">{t('ollamaModels.catalog.table.uploaded')}</th>
              </tr>
            </thead>
            <tbody>
              {modelsLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={6}>
                    {t('ollamaModels.catalog.loading')}
                  </td>
                </tr>
              )}
              {modelsError && (
                <tr>
                  <td className="px-4 py-3 text-danger" colSpan={6}>
                    {String(modelsError)}
                  </td>
                </tr>
              )}
              {models?.models.length === 0 && !modelsLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={6}>
                    <Trans
                      i18nKey="ollamaModels.catalog.empty"
                      values={{
                        cmd: `OLLAMA_HOST=<host>/ollama/${projectSlug}/${repo.slug}`,
                      }}
                      components={[<code key="0" className="orb-code" />, <code key="1" className="orb-code" />]}
                    />
                  </td>
                </tr>
              )}
              {models?.models.map((m) => {
                const isSelected =
                  selected?.model === m.model && selected?.tag === m.tag;
                return (
                  <tr
                    key={`${m.model}:${m.tag}`}
                    className={`border-b border-border/50 cursor-pointer ${
                      isSelected ? 'bg-bg-hover/60' : 'hover:bg-bg-hover/40'
                    }`}
                    onClick={() => setSelected(m)}
                  >
                    <td className="px-4 py-2 font-medium text-fg">{m.model}</td>
                    <td className="px-4 py-2">
                      <Badge tone="accent">{m.tag}</Badge>
                    </td>
                    <td className="px-4 py-2 tabular-nums text-fg-muted">
                      {m.layer_count}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-fg-muted">
                      {bytes(m.total_size_bytes)}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-fg-muted">
                      {shortHex(m.digest)}
                    </td>
                    <td className="px-4 py-2 text-fg-muted">
                      {new Date(m.uploaded_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {models && (
        <Card>
          <CardHeader title={t('ollamaModels.pull.title')} />
          <CardBody>
            <PullSnippet hint={models.pull_hint} />
          </CardBody>
        </Card>
      )}

      {selected && (
        <Card>
          <CardHeader
            title={
              <span className="inline-flex items-center gap-2">
                <Layers className="h-4 w-4 text-accent" />
                {selected.model}
                <Badge tone="accent">{selected.tag}</Badge>
              </span>
            }
            description={t('ollamaModels.detail.description')}
          />
          <CardBody>
            {detail.isLoading && <p className="text-fg-muted">{t('ollamaModels.detail.loading')}</p>}
            {detail.error && (
              <p className="text-danger">{String(detail.error)}</p>
            )}
            {detail.data && <ModelDetail detail={detail.data} />}
          </CardBody>
        </Card>
      )}
    </div>
  );
}

interface PullSnippetProps {
  readonly hint: string;
}

function PullSnippet({ hint }: PullSnippetProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(hint);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard write can fail in non-secure contexts; the operator can
      // always select the snippet manually so we don't surface an error.
    }
  };
  return (
    <div className="flex items-start gap-3">
      <pre className="flex-1 bg-bg-inset border border-border rounded-md p-3 text-xs font-mono text-fg overflow-x-auto whitespace-pre">
        {hint}
      </pre>
      <Button
        variant="ghost"
        type="button"
        onClick={onCopy}
        title={t('ollamaModels.pull.copySnippet')}
      >
        <Copy className="h-4 w-4" />
        {copied ? t('tokens.created.copied') : t('tokens.created.copy')}
      </Button>
    </div>
  );
}

interface ModelDetailProps {
  readonly detail: {
    readonly model: string;
    readonly tag: string;
    readonly digest: string;
    readonly size_bytes: number;
    readonly uploaded_at: string;
    readonly metadata: Record<string, unknown>;
    readonly pull_hint: string;
  };
}

function ModelDetail({ detail }: ModelDetailProps) {
  const { t } = useTranslation();
  const meta = detail.metadata ?? {};
  const layers = (meta.layers as OllamaLayer[] | undefined) ?? [];
  const total = (meta.total_size as number | undefined) ?? 0;
  const configDigest = meta.config_digest as string | undefined;

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">
            {t('ollamaModels.modelDetail.manifestDigest')}
          </dt>
          <dd className="font-mono text-xs text-fg break-all">{detail.digest}</dd>
        </div>
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">
            {t('ollamaModels.modelDetail.totalSize')}
          </dt>
          <dd className="tabular-nums text-fg">{bytes(total || detail.size_bytes)}</dd>
        </div>
        {configDigest && (
          <div>
            <dt className="text-fg-muted text-xs uppercase tracking-wide">
              {t('ollamaModels.modelDetail.configDigest')}
            </dt>
            <dd className="font-mono text-xs text-fg break-all">{configDigest}</dd>
          </div>
        )}
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">
            {t('ollamaModels.modelDetail.uploaded')}
          </dt>
          <dd className="text-fg-muted">
            {new Date(detail.uploaded_at).toLocaleString()}
          </dd>
        </div>
      </dl>

      <div>
        <h3 className="text-sm font-semibold text-fg mb-2">
          {t('ollamaModels.modelDetail.layersTitle', { count: layers.length })}
        </h3>
        {layers.length === 0 ? (
          <p className="text-fg-muted text-sm">{t('ollamaModels.modelDetail.noLayers')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-fg-muted border-b border-border">
                  <th className="px-3 py-2 font-medium">
                    {t('ollamaModels.modelDetail.table.kind')}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t('ollamaModels.modelDetail.table.mediaType')}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t('ollamaModels.modelDetail.table.digest')}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t('ollamaModels.modelDetail.table.size')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {layers.map((l) => {
                  const { label, icon: Icon } = layerKindLabel(l.media_type, t);
                  return (
                    <tr key={l.digest} className="border-b border-border/50">
                      <td className="px-3 py-2 text-fg">
                        <span className="inline-flex items-center gap-2">
                          <Icon className="h-3.5 w-3.5 text-accent" />
                          {label}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-fg-muted">
                        {l.media_type}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-fg-muted">
                        {shortHex(l.digest)}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-fg-muted">
                        {bytes(l.size)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PullSnippet hint={detail.pull_hint} />
    </div>
  );
}
